import { Injectable, inject, signal } from "@angular/core";
import { HttpHelperService } from "@helpers/http";
import {
  injectQuery,
  injectMutation,
  injectQueryClient,
  toPromise,
  injectInfiniteQuery,
} from "@ngneat/query";
import { environment } from "src/environments/environment";
import {
  TMenuProductCategoryDTO,
  TMenuProductContentDTO,
  TMenuProductOptionDTO,
} from "./interfaces";
import * as qs from "qs";
import { map } from "rxjs";

import { AlertController, ToastController } from "@ionic/angular";
import { StatusCode } from "@helpers/common/interfaces";

import { IMerchant } from "@api/merchant/interfaces";
@Injectable({
  providedIn: "root",
})
export class MenuDishApiService {
  #query = injectQuery();
  #queryInfinite = injectInfiniteQuery();

  #mutation = injectMutation();
  #client = injectQueryClient();
  #http = inject(HttpHelperService);
  #coreEndPoint = environment.coreEndPoint;
  #mch = signal<IMerchant>(null);
  private alertCtrl = inject(AlertController);
  private toastCtrl = inject(ToastController);

  constructor() {}

  getList(menuId: number, searchQuery = {}) {
    return this.#queryInfinite({
      queryKey: ["menuDishes", searchQuery],
      queryFn: ({ pageParam, signal }) => {
        const queries = qs.stringify({
          ...searchQuery,
          ...{ skip: pageParam },
        });
        const path = `admin/menu/${menuId}/products?${queries}`;
        return toPromise({
          source: this.#http.get(path).pipe(map((res) => res.data)),
          signal,
        });
      },
      // staleTime: 5000,
      initialPageParam: 0,
      getPreviousPageParam: (firstPage) => null,
      getNextPageParam: (lastPage) => {
        if (!lastPage) return null;
        const { count, skip, limit } = lastPage;
        if (count <= skip + limit) return null;
        return lastPage.skip + lastPage.limit;
      },
    });
  }

  getById(mid: number, pid: number) {
    return this.#query({
      queryKey: ["menuDish"] as const,

      queryFn: () => {
        const path = `admin/menu/${mid}/product/${pid}`;
        return this.#http.get(path).pipe(map((res) => res.data.product));
      },
    });
  }

  addDish() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({});
        this.presentToast("Dish created !");
      },
      onError: this.errorHandler,
      mutationFn: ({
        mid,
        body,
      }: {
        mid: number;
        body: { categories?: number[]; data: TMenuProductContentDTO };
      }) => {
        const path = `admin/menu/${mid}/product/add`;
        return this.#http.post(path, body).pipe(map((res) => res.data));
      },
    });
  }

  updateProductTax<T>() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries();
      },
      onError: this.errorHandler,
      mutationFn: ({
        mid,
        pid,
        data,
      }: {
        mid: number;
        pid: number;
        data: TMenuProductContentDTO;
      }) => {
        const path = `admin/menu/${mid}/product/${pid}/tax`;
        return this.#http.put(path, { data }).pipe(map((res) => res.data));
      },
    });
  }

  updateStatus<T>() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({});
      },
      onError: this.errorHandler,
      mutationFn: ({ mid, pid, status }) => {
        const path = `admin/menu/${mid}/product/${pid}/status`;
        return this.#http.put(path, { status }).pipe(map((res) => res.data));
      },
    });
  }

  updateStockStatus<T>() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({});
      },
      onError: this.errorHandler,
      mutationFn: ({ mid, pid, status }) => {
        const path = `admin/menu/${mid}/product/${pid}/stock`;
        return this.#http.put(path, { status }).pipe(map((res) => res.data));
      },
    });
  }

  updateStockStatusBatch<T>() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({});
        this.presentToast("Stock status updated !");
      },
      onError: this.errorHandler,
      mutationFn: ({
        mid,
        body,
      }: {
        mid: number;
        body: { status: StatusCode; products: number[] };
      }) => {
        const path = `admin/menu/${mid}/products/stock`;

        return this.#http.put(path, body).pipe(map((res) => res.data));
      },
    });
  }

  updateDiscountableStatus<T>() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({});
      },
      onError: this.errorHandler,
      mutationFn: ({ mid, pid, status }) => {
        const path = `admin/menu/${mid}/product/${pid}/discountable`;
        return this.#http.put(path, { status }).pipe(map((res) => res.data));
      },
    });
  }

  updateContent<T>() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({});
        this.presentToast("Content Updated !");
      },
      onError: this.errorHandler,
      mutationFn: ({ mid, pid, body }) => {
        const path = `admin/menu/${mid}/product/${pid}/content`;
        return this.#http.put(path, body).pipe(map((res) => res.data));
      },
    });
  }

  updateCategory<T>() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({
          queryKey: ["menuDish"],
        });

        this.presentToast("categories Updated !");
      },
      onError: this.errorHandler,
      mutationFn: ({
        mid,
        pid,
        body,
      }: {
        mid: number;
        pid: number;
        body: TMenuProductCategoryDTO;
      }) => {
        const path = `admin/menu/${mid}/product/${pid}/related/categories`;

        return this.#http.put(path, body).pipe(map((res) => res.data));
      },
    });
  }

  updateOption<T>() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({
          queryKey: ["menuDish"],
        });

        this.presentToast("Options Updated !");
      },
      onError: this.errorHandler,
      mutationFn: ({
        mid,
        pid,
        body,
      }: {
        mid: number;
        pid: number;
        body: TMenuProductOptionDTO;
      }) => {
        const path = `admin/menu/${mid}/product/${pid}/related/options`;

        return this.#http.put(path, body).pipe(map((res) => res.data));
      },
    });
  }

  updateOptionPosition() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({});
        this.presentToast("Option orders Updated !");
      },
      onError: this.errorHandler,
      mutationFn: ({ mid, pid, body }) => {
        const path = `admin/menu/${mid}/product/${pid}/related/options/position`;

        return this.#http.put(path, body).pipe(map((res) => res.data));
      },
    });
  }

  addCategory<T>() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({});
        this.presentToast("Content Updated !");
      },
      onError: this.errorHandler,
      mutationFn: ({
        mid,
        pid,
        body,
      }: {
        mid: number;
        pid: number;
        body: {
          categories: number[];
        };
      }) => {
        const path = `admin/menu/${mid}/product/${pid}/related/categories/add`;
        return this.#http.put(path, body).pipe(map((res) => res.data));
      },
    });
  }

  removeCategory<T>() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({});
        this.presentToast("Content Updated !");
      },
      onError: this.errorHandler,
      mutationFn: ({
        mid,
        pid,
        body,
      }: {
        mid: number;
        pid: number;
        body: {
          categories: number[];
        };
      }) => {
        const path = `admin/menu/${mid}/product/${pid}/related/categories/remove`;
        return this.#http.put(path, body).pipe(map((res) => res.data));
      },
    });
  }

  getUpcList(menuId: number, searchQuery = {}) {
    return this.#queryInfinite({
      queryKey: ["menuUpcList", searchQuery],
      queryFn: ({ pageParam, signal }) => {
        const queries = qs.stringify({
          ...searchQuery,
          ...{ skip: pageParam },
        });
        const path = `admin/menu/${menuId}/products/upc?${queries}`;
        return toPromise({
          source: this.#http.get(path).pipe(map((res) => res.data)),
          signal,
        });
      },
      // staleTime: 5000,
      initialPageParam: 0,
      getPreviousPageParam: (firstPage) => null,
      getNextPageParam: (lastPage) => {
        if (!lastPage) return null;
        const { count, skip, limit } = lastPage;
        if (count <= skip + limit) return null;
        return lastPage.skip + lastPage.limit;
      },
    });
  }

  updateBundledCount<T>() {
    return this.#mutation({
      onSuccess: () => {
        this.#client.invalidateQueries({});

        this.presentToast("Bundled Dish Updated !");
      },
      onError: this.errorHandler,
      mutationFn: ({
        mid,
        pid,
        body,
      }: {
        mid: number;
        pid: number;
        body: {
          products: { upc: string; count: number }[];
        };
      }) => {
        const path = `admin/menu/${mid}/product/${pid}/related/products/update`;

        return this.#http.put(path, body).pipe(map((res) => res.data));
      },
    });
  }

  private errorHandler = async (error: any, variables, context) => {
    if (typeof error === "string") {
      const alert = await this.alertCtrl.create({
        header: `Error`,
        // subHeader: "Error",
        message: error,
        buttons: ["OK"],
      });

      alert.present();
    } else {
      const alert = await this.alertCtrl.create({
        header: error.name,
        subHeader: error.message,
        message: error.problems ? error.problems.join(", ") : "",
        buttons: ["OK"],
      });

      alert.present();
    }

    if (error.error instanceof ProgressEvent) {
      const alert = await this.alertCtrl.create({
        header: `ProgressEvent Error`,
        // subHeader: "Error",
        message: "Please try later",
        buttons: ["OK"],
      });

      alert.present();
    } else if (error.error) {
      const alert = await this.alertCtrl.create({
        header: error.error.name,
        subHeader: error.error.message,
        message: error.error.problems ? error.error.problems.join(", ") : "",
        buttons: ["OK"],
      });

      alert.present();
    }
  };

  private async presentToast(
    message: string,
    color: "danger" | "success" | "warning" = "success",
  ) {
    const toast = await this.toastCtrl.create({
      message,
      duration: 3000,
      buttons: [
        {
          text: "Dismiss",
          role: "cancel",
          handler: () => {
            console.log("Dismiss clicked");
          },
        },
      ],
      position: "top",
      positionAnchor: "header",
      color,
      swipeGesture: "vertical",
    });

    await toast.present();
  }
}
